import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Hero.css';

const Hero = ({
    title = "TimbrLux Stays",
    tagline = "The best home experience, blending comfort, style, and serene settings.",
    ctaText = "Plan your escape!",
    ctaLink = "/booking",
    backgroundImage = null
}) => {
    // Defer loading the background video so the poster image is the LCP and the
    // video does not compete with critical resources on first paint.
    const [loadVideo, setLoadVideo] = useState(false);

    useEffect(() => {
        const start = () => setLoadVideo(true);
        if ('requestIdleCallback' in window) {
            const id = window.requestIdleCallback(start, { timeout: 2500 });
            return () => window.cancelIdleCallback(id);
        }
        const timer = setTimeout(start, 1200);
        return () => clearTimeout(timer);
    }, []);
    // Schema.org structured data for LodgingBusiness
    const heroSchema = {
        "@context": "https://schema.org",
        "@type": "LodgingBusiness",
        "name": title,
        "description": tagline,
        "url": "https://www.timbrluxstays.com",
        "priceRange": "$$$",
        "@id": "https://www.timbrluxstays.com/#organization",
        "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [".hero-title", ".hero-tagline"]
        }
    };

    // VideoObject schema for hero video
    const videoSchema = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": "TimbrLux Stays - Luxury Vacation Rentals",
        "description": "Experience beautifully designed vacation homes at TimbrLux Stays. Watch our property showcase.",
        "thumbnailUrl": "https://www.timbrluxstays.com/image/homeHeroBg.webp",
        "uploadDate": "2024-01-01",
        "contentUrl": "https://www.timbrluxstays.com/hero_bg_video.mp4",
        "embedUrl": "https://www.timbrluxstays.com",
        "duration": "PT30S"
    };

    const bgImage = backgroundImage || `${process.env.PUBLIC_URL}/image/homeHeroBg.webp`;

    return (
        <section
            className="hero"
            role="banner"
            aria-label="Hero section"
            itemScope
            itemType="https://schema.org/LodgingBusiness"
        >
            {/* Background Video - deferred so the poster image is the LCP */}
            <video
                autoPlay
                muted
                loop
                playsInline
                preload="none"
                poster={bgImage}
                className="hero-video"
                key={loadVideo ? 'video-loaded' : 'video-idle'}
            >
                {loadVideo && (
                    <source src={`${process.env.PUBLIC_URL}/hero_bg_video.mp4`} type="video/mp4" />
                )}
                Your browser does not support the video tag.
            </video>

            {/* Schema.org JSON-LD */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(heroSchema) }}
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(videoSchema) }}
            />

            {/* Dark Overlay */}
            <div className="hero-overlay" aria-hidden="true" />

            {/* Content */}
            <div className="hero-content">
                <div className="hero-text">
                    <h1 className="hero-title" itemProp="name">
                        {title}
                    </h1>
                    <p className="hero-tagline" itemProp="description">
                        {tagline}
                    </p>
                </div>

                <Link
                    to={ctaLink}
                    className="hero-cta"
                    aria-label={ctaText}
                    itemProp="potentialAction"
                    itemScope
                    itemType="https://schema.org/ReserveAction"
                >
                    <span itemProp="name">{ctaText}</span>
                </Link>
            </div>
        </section>
    );
};

export default Hero;
